/**
 * GlobalSearch — Search bar with results dropdown.
 *
 * Rendered inline on Assets, Analysis, and Assemblies pages (inline prop).
 * Also used as mobile overlay via MobileNav.
 * Uses GET /moments/ with PostgreSQL full-text search (q param).
 * Cmd+K / Ctrl+K focuses from anywhere.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, X, Film, Loader2 } from 'lucide-react'
import apiClient from '../services/api'
import { useMediaPreview } from '../hooks/useMediaPreview'
import MediaPreviewModal from './MediaPreviewModal'

interface SearchResult {
  id: number
  asset_id: number | null
  title: string | null
  visual_description: string | null
  thumbnail_url: string | null
  asset_filename: string | null
  asset_file_type: string | null
  start_time: number | null
  end_time: number | null
  tags: string[] | null
}

function formatDuration(start: number | null, end: number | null): string | null {
  if (start == null || end == null) return null
  const dur = end - start
  if (dur <= 0) return null
  const m = Math.floor(dur / 60)
  const s = Math.floor(dur % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export default function GlobalSearch({
  isCollapsed = false,
  isMobile,
  onClose,
  inline,
}: {
  isCollapsed?: boolean
  isMobile?: boolean
  onClose?: () => void
  inline?: boolean
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [expandedFromCollapsed, setExpandedFromCollapsed] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const { preview, closePreview, openFromAsset, openImage } = useMediaPreview()

  // Debounced search
  const doSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([])
      setTotal(0)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await apiClient.get('/moments/', {
        params: { q: q.trim(), limit: 20 },
      })
      setResults(res.data.moments || [])
      setTotal(res.data.total || 0)
    } catch {
      setResults([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleInputChange = (val: string) => {
    setQuery(val)
    setIsOpen(true)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => doSearch(val), 300)
  }

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (isCollapsed) setExpandedFromCollapsed(true)
        setIsOpen(true)
        setTimeout(() => inputRef.current?.focus(), 50)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isCollapsed])

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setExpandedFromCollapsed(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [isOpen])

  // Escape to close
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
        setExpandedFromCollapsed(false)
        inputRef.current?.blur()
        onClose?.()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  const handleResultClick = async (result: SearchResult) => {
    const title = result.title || result.asset_filename || 'Preview'
    if (result.asset_id) {
      await openFromAsset({
        assetId: result.asset_id,
        fileType: result.asset_file_type,
        title,
        fallbackImageUrl: result.thumbnail_url || null,
      })
    } else if (result.thumbnail_url) {
      openImage(result.thumbnail_url, title)
    }
    setIsOpen(false)
    setExpandedFromCollapsed(false)
    onClose?.()
  }

  const clear = () => {
    setQuery('')
    setResults([])
    setTotal(0)
    setIsOpen(false)
    setExpandedFromCollapsed(false)
  }

  // Collapsed sidebar: show icon only (not in inline mode)
  if (!inline && isCollapsed && !expandedFromCollapsed && !isMobile) {
    return (
      <>
        <button
          onClick={() => {
            setExpandedFromCollapsed(true)
            setIsOpen(true)
            setTimeout(() => inputRef.current?.focus(), 50)
          }}
          className="w-full flex items-center justify-center py-2.5 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
          title="Search (⌘K)"
        >
          <Search className="w-5 h-5" />
        </button>
        <MediaPreviewModal preview={preview} onClose={closePreview} />
      </>
    )
  }

  // Expanded from collapsed: floating panel
  const isFloating = !inline && isCollapsed && expandedFromCollapsed && !isMobile

  return (
    <>
      <div
        ref={containerRef}
        className={
          inline
            ? 'relative'
            : isMobile
              ? 'px-4 pt-3 pb-2'
              : isFloating
                ? 'fixed left-20 top-4 z-[60] w-72'
                : 'px-3 py-2'
        }
      >
        {/* Search input */}
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
          isOpen
            ? 'border-[var(--rq-blue)]/40 bg-white/[0.06]'
            : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.05]'
        } ${isFloating ? 'shadow-xl bg-[var(--rq-surface)] border-white/15' : ''}`}>
          <Search className="w-4 h-4 text-[var(--rq-text-soft)] flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => handleInputChange(e.target.value)}
            onFocus={() => { if (query.trim()) setIsOpen(true) }}
            placeholder="A wide shot of the city at sunset..."
            className="flex-1 bg-transparent text-sm text-[var(--rq-text)] placeholder:text-[var(--rq-text-soft)]/50 outline-none min-w-0"
          />
          {query ? (
            <button onClick={clear} className="flex-shrink-0 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]">
              <X className="w-3.5 h-3.5" />
            </button>
          ) : (
            <kbd className="hidden sm:inline text-[9px] text-[var(--rq-text-soft)]/50 border border-white/10 rounded px-1 py-0.5 flex-shrink-0">
              ⌘K
            </kbd>
          )}
        </div>

        {/* Results dropdown */}
        {isOpen && (query.trim() || loading) && (
          <div className={`mt-1 rounded-lg border border-white/10 bg-[var(--rq-surface)] shadow-xl overflow-hidden ${
            inline ? 'absolute left-0 right-0' : isFloating ? '' : 'absolute left-3 right-3'
          } z-50 max-h-80 overflow-y-auto`}>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-4 h-4 animate-spin text-[var(--rq-text-soft)]" />
              </div>
            ) : results.length > 0 ? (
              <>
                <div className="px-3 py-1.5 border-b border-white/[0.06]">
                  <p className="text-[10px] text-[var(--rq-text-soft)]">
                    {total} result{total !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="divide-y divide-white/[0.04]">
                  {results.map(r => {
                    const dur = formatDuration(r.start_time, r.end_time)
                    return (
                      <button
                        key={r.id}
                        onClick={() => handleResultClick(r)}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/[0.04] transition-colors text-left"
                      >
                        <div className="w-10 h-7 rounded overflow-hidden bg-black/40 flex-shrink-0">
                          {r.thumbnail_url ? (
                            <img src={r.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film className="w-3 h-3 text-[var(--rq-text-soft)]" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[var(--rq-text)] truncate">
                            {r.title || r.asset_filename || 'Untitled'}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {dur && (
                              <span className="text-[10px] text-[var(--rq-text-soft)] tabular-nums">
                                {dur}
                              </span>
                            )}
                            {r.asset_filename && r.title && (
                              <span className="text-[10px] text-[var(--rq-text-soft)] truncate">
                                {r.asset_filename}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </>
            ) : query.trim() ? (
              <div className="py-6 text-center text-xs text-[var(--rq-text-soft)]">
                No results for &ldquo;{query}&rdquo;
              </div>
            ) : null}
          </div>
        )}
      </div>

      <MediaPreviewModal preview={preview} onClose={closePreview} />
    </>
  )
}
